import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'

import {
  BackgroundRun,
  cancelBackgroundRun,
  dismissBackgroundRun,
  downloadBackgroundRun,
  useBackgroundRuns,
} from './store'

export * from './store'

const statusLabel: Record<BackgroundRun['status'], string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Ready',
  error: 'Failed',
  canceled: 'Canceled',
}

const statusClass: Record<BackgroundRun['status'], string> = {
  queued: 'text-gray-500',
  running: 'text-shade-blue',
  done: 'text-green-600',
  error: 'text-red-600',
  canceled: 'text-gray-500',
}

const Spinner = () => (
  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-shade-blue border-t-transparent" />
)

/**
 * The tray for runs sent to the background: servicing reports and statement batches.
 *
 * Mounted in the base layout so it survives navigating away from the screen that started the run,
 * which is the whole point of the background option. It renders nothing when there is nothing to
 * show, and opens itself the first time a run lands so "tell me when it is ready" does not depend
 * on the user having noticed a badge.
 *
 * It sits clear of the right edge, which the floater tabs and the assistant bubble own.
 */
export const BackgroundRuns = () => {
  const runs = useBackgroundRuns()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string>('')
  const active = (run: BackgroundRun) => run.status === 'running' || run.status === 'queued'
  const finished = runs.filter((run) => !active(run)).length

  // Open on the first completion, not on every one: a user who deliberately collapsed the tray
  // while three runs are in flight should not have it spring back twice more.
  const [announced, setAnnounced] = useState(0)
  useEffect(() => {
    if (finished > announced) setOpen(true)
    setAnnounced(finished)
  }, [finished, announced])

  if (!runs.length) return null

  const running = runs.filter(active).length
  const ready = runs.filter((run) => run.status === 'done').length

  const act = async (jobId: string, fn: () => Promise<void>) => {
    setBusy(jobId)
    try {
      await fn()
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="fixed bottom-4 right-24 z-30 w-[22rem] max-w-[calc(100vw-8rem)]">
      <div className="rounded-lg border border-gray-200 bg-white shadow-lg">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold text-shade-blue"
          onClick={() => setOpen(!open)}
        >
          {running ? <Spinner /> : null}
          <span className="flex-1">
            Background runs
            <span className="ml-1 font-normal text-gray-500">
              ({running ? `${running} running` : `${ready} ready`})
            </span>
          </span>
          {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
        </button>

        {open ? (
          <div className="max-h-[50vh] overflow-auto border-t border-gray-100">
            {runs.map((run) => (
              <div key={run.jobId} className="flex items-start gap-2 border-b border-gray-50 px-3 py-2 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-800" title={run.label}>
                    {run.label}
                  </p>
                  <p className={`text-xs ${statusClass[run.status]}`}>
                    {statusLabel[run.status]}
                    {run.detail ? <span className="text-gray-500"> · {run.detail}</span> : null}
                  </p>
                  {run.error ? (
                    <p className="mt-0.5 flex items-start gap-1 text-xs text-red-600">
                      <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                      <span>{run.error}</span>
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {run.status === 'done' && (run.kind === 'report' || run.kind === 'hmdaPipe') ? (
                    <button
                      type="button"
                      title="Download"
                      disabled={busy === run.jobId}
                      className="rounded p-1 text-shade-blue hover:bg-gray-100 disabled:opacity-50"
                      onClick={() => act(run.jobId, () => downloadBackgroundRun(run.jobId))}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                  {active(run) ? (
                    <button
                      type="button"
                      className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                      disabled={busy === run.jobId}
                      onClick={() => act(run.jobId, () => cancelBackgroundRun(run.jobId))}
                    >
                      Cancel
                    </button>
                  ) : (
                    <button
                      type="button"
                      title="Dismiss"
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      onClick={() => act(run.jobId, () => dismissBackgroundRun(run.jobId))}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
